import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { CircleCheck, Clock, House, Info, Lock, RotateCw, Store, Wallet } from 'lucide-react';

import { deliveriesApi } from '@/api/deliveries.api';
import type { DeliveryDetail, DeliveryStatus } from '@/api/types';
import { Main, PageHeader } from '@/components/app-shell';
import { DeliveryTimeline } from '@/components/delivery-timeline';
import { DetailBlock } from '@/components/detail-block';
import { FailSheet } from '@/components/fail-sheet';
import { HandoverSheet } from '@/components/handover-sheet';
import { PickupCodeDialog } from '@/components/pickup-code-dialog';
import { ErrorState, SectionLabel } from '@/components/states';
import { TripMap } from '@/components/trip-map';
import { useRiderSession } from '@/auth/rider-session';
import { StatusPill } from '@/components/status-pill';
import { Button } from '@/components/ui/button';
import { Card, CardBody } from '@/components/ui/card';
import { Skeleton, LoadingAnnouncer } from '@/components/ui/skeleton';
import { toast } from '@/lib/toast';
import { BatchPickupSheet } from '@/components/batch-pickup-sheet';
import { useDelivery, useDeliveryCache, useRunSheet } from '@/hooks/use-deliveries';
import { SHOW_HANDOVER_CODE } from '@/lib/env';
import { ApiError, errorMessage } from '@/lib/errors';
import { formatElapsed, formatMoney, isZeroMoney } from '@/lib/format';
import { addressOneLine, isPinned, routeUrl } from '@/lib/navigation-links';
import { RouteSummary } from '@/components/route-summary';
import { legFor, readyAtSamePharmacy, toPoint } from '@/lib/route';
import { isCashOnDelivery, isInHand, isTerminal, legHint } from '@/lib/status';

/**
 * One job, and the single thing to do next about it.
 *
 * The whole screen is built around there being exactly ONE primary action at
 * any moment, pinned to the bottom where a thumb reaches. A rider holding a
 * bag on a staircase should not have to decide which of three buttons
 * describes what they just did, and a screen that offered "picked up" and "on
 * my way" side by side would collect both taps in the wrong order.
 */
export function DeliveryScreen() {
  const { deliveryId = '' } = useParams();
  const query = useDelivery(deliveryId);
  const cache = useDeliveryCache();
  // Where the phone last reported being — the "You" badge on the trip map.
  const { ping } = useRiderSession();

  const [working, setWorking] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [handoverOpen, setHandoverOpen] = useState(false);
  const [failOpen, setFailOpen] = useState(false);
  const [pickupCode, setPickupCode] = useState<string | null>(null);
  const [batchOpen, setBatchOpen] = useState(false);

  const delivery = query.data;
  const { jobs } = useRunSheet();

  /*
   * The other bags packed and waiting at THIS counter.
   *
   * Read off the run sheet rather than fetched: the sheet is already loaded,
   * already polling, and already carries every field this needs. Includes the
   * delivery on screen, so the count the button shows is the number of bags
   * the rider will walk out with.
   */
  const atThisCounter = readyAtSamePharmacy(jobs, delivery?.pharmacyId);

  /**
   * Runs one transition and redraws from what came back.
   *
   * Every transition endpoint answers with the whole updated delivery, so the
   * screen redraws from the response rather than refetching — one round trip
   * instead of two, on the worst connection in the system.
   */
  async function advance(action: () => Promise<DeliveryDetail>) {
    if (working) return;
    setWorking(true);
    setActionError(null);
    try {
      const updated = await action();
      cache.write(updated);
      if (isTerminal(updated.status)) cache.settled(updated.id);
    } catch (error) {
      setActionError(errorMessage(error));
      /*
       * A 409 means the state moved underneath us — dispatch unassigned the
       * job, the shop has not packed it, or the tap landed twice. The screen
       * is now describing something that is no longer true, so it is refetched
       * rather than left showing a button that will fail the same way again.
       */
      if (error instanceof ApiError && error.isStaleState) cache.invalidate(deliveryId);
    } finally {
      setWorking(false);
    }
  }

  if (query.isPending) {
    return (
      <>
        <PageHeader title="Delivery" back />
        <Main className="grid gap-4 pt-4">
          <LoadingAnnouncer label="Loading this delivery" />
          <Skeleton className="h-24" />
          <Skeleton className="h-36" />
          <Skeleton className="h-36" />
        </Main>
      </>
    );
  }

  if (!delivery) {
    return (
      <>
        <PageHeader title="Delivery" back />
        <Main className="pt-4">
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        </Main>
      </>
    );
  }

  const cod = isCashOnDelivery(delivery) && !isZeroMoney(delivery.codAmount);
  const carrying = isInHand(delivery.status);
  const address = delivery.deliveryAddress;
  /*
   * The backend WITHHOLDS the destination until the bag is in hand, and this
   * app must not pretend otherwise. Distinguished from an order with no
   * address on it at all, which would be a data problem rather than a policy
   * one, and wants different words.
   */
  const destinationWithheld = !address && !carrying;
  const pharmacyPlace = {
    latitude: delivery.pharmacyLatitude,
    longitude: delivery.pharmacyLongitude,
    query: [delivery.pharmacyName, delivery.pharmacyAddress].filter(Boolean).join(' '),
  };
  const shop = toPoint(delivery.pharmacyLatitude, delivery.pharmacyLongitude);
  const drop = address ? toPoint(address.latitude, address.longitude) : null;
  // The server's estimate, or the same one worked out here once both pins are known.
  const leg = legFor(delivery.route, shop, drop);
  const showMap = !isTerminal(delivery.status) && Boolean(shop || drop);
  const tripUrl = address
    ? routeUrl(pharmacyPlace, {
        latitude: address.latitude,
        longitude: address.longitude,
        query: addressOneLine(address),
      })
    : null;

  return (
    <>
      <PageHeader
        title={delivery.orderNumber ?? 'Delivery'}
        back
        action={
          <Button
            variant="ghost"
            size="icon"
            onClick={() => void query.refetch()}
            loading={query.isFetching}
          >
            <RotateCw aria-hidden="true" />
            <span className="sr-only-text">Refresh this delivery</span>
          </Button>
        }
      />

      <Main className="pt-4">
        <div className="grid gap-4 pb-4">
        <div className="flex items-center justify-between gap-3">
          <StatusPill status={delivery.status} size="md" />
          <span data-numeric className="flex items-center gap-1 text-xs text-ink-subtle">
            <Clock className="size-3.5" aria-hidden="true" />
            Opened {formatElapsed(delivery.createdAt)} ago
          </span>
        </div>

        {cod ? (
          <Card variant="danger">
            <CardBody className="flex items-center gap-3">
              <Wallet className="size-5 shrink-0 text-error-ink" aria-hidden="true" />
              <p data-numeric className="font-semibold leading-snug text-error-ink">
                Cash on delivery — collect {formatMoney(delivery.codAmount)}
              </p>
            </CardBody>
          </Card>
        ) : null}

        <section aria-labelledby="pickup-heading">
          <h2 id="pickup-heading" className="eyebrow pb-2">
            Collect from
          </h2>
          <DetailBlock
            icon={Store}
            label={delivery.pharmacyName ?? 'Pharmacy'}
            value={delivery.pharmacyAddress ?? 'Address not on file'}
            hint={legHint(delivery)}
            phone={delivery.pharmacyPhone}
            phoneLabel="Call the shop"
            directions={
              delivery.pharmacyAddress || delivery.pharmacyLatitude ? pharmacyPlace : null
            }
          />
        </section>

        <section aria-labelledby="dropoff-heading">
          <h2 id="dropoff-heading" className="eyebrow pb-2">
            Deliver to
          </h2>

          {destinationWithheld ? (
            /*
             * Two different reasons the door is hidden, and they want
             * different words. Before pickup it has not opened yet; after the
             * job ends the server closes it again, and "shown once you collect
             * the bag" under a delivered order reads as a bug.
             */
            <DetailBlock
              icon={Lock}
              tone="muted"
              label={delivery.customerName ?? 'Customer'}
              value={
                isTerminal(delivery.status)
                  ? 'Hidden now this job is finished'
                  : 'Shown once you collect the bag'
              }
              hint={
                isTerminal(delivery.status)
                  ? "A customer's address and number are only open while their order is in your bag."
                  : 'Addresses and phone numbers open up at pickup.'
              }
            />
          ) : (
            <DetailBlock
              icon={House}
              label={delivery.customerName ?? 'Customer'}
              value={addressOneLine(address) || 'No address on this order'}
              hint={
                address && !isPinned(address)
                  ? 'No map pin on this address — ring ahead if you cannot find it.'
                  : null
              }
              phone={delivery.customerPhone}
              phoneLabel="Call the customer"
              directions={
                address
                  ? {
                      latitude: address.latitude,
                      longitude: address.longitude,
                      query: addressOneLine(address),
                    }
                  : null
              }
            />
          )}

        </section>

        {/*
          * The trip between the two ends: how far and how long, then the
          * picture of it. Before pickup only the numbers — the server can
          * measure a trip it will not yet say the end of, and a map of one
          * pin is worse than none. Once the bag is in hand, both pins, the
          * line between them, and a tap into the real route in Maps.
          */}
        {leg || showMap ? (
          <section aria-labelledby="trip-heading" className="grid gap-3">
            <h2 id="trip-heading" className="eyebrow">
              Trip
            </h2>
            <RouteSummary route={leg} href={tripUrl} />
            {/*
              * Live while the job is: before pickup it is the shop and "you",
              * which is the leg the rider is riding; after it, all three. A
              * finished job has nothing left to ride to.
              */}
            {showMap ? <TripMap shop={shop} door={drop} rider={ping.position} /> : null}
          </section>
        ) : null}

        <section aria-labelledby="progress-heading">
          <h2 id="progress-heading" className="eyebrow pb-3 pt-2">
            Progress
          </h2>
          <DeliveryTimeline job={delivery} />
        </section>

        {delivery.proofOfDeliveryUrl ? (
          <section aria-labelledby="proof-heading">
            <SectionLabel className="pt-2">
              <span id="proof-heading">Proof of delivery</span>
            </SectionLabel>
            <img
              src={delivery.proofOfDeliveryUrl}
              alt="The photograph taken at the door"
              className="w-full rounded-xl border border-line object-cover"
              loading="lazy"
            />
          </section>
        ) : null}

        {actionError ? (
          <p
            role="alert"
            className="rounded-lg border border-error-line/60 bg-error-surface px-4 py-3 text-sm font-medium text-error-ink"
          >
            {actionError}
          </p>
        ) : null}
        </div>

        <ActionBar
          delivery={delivery}
          working={working}
          batchCount={atThisCounter.length}
          onBatch={() => setBatchOpen(true)}
          onAdvance={advance}
          onCollect={() =>
            void advance(async () => {
              const result = await deliveriesApi.pickUp(delivery.id);
              if (SHOW_HANDOVER_CODE && result.customerOtp) setPickupCode(result.customerOtp);
              return result.delivery;
            })
          }
          onHandover={() => setHandoverOpen(true)}
          onGiveUp={() => setFailOpen(true)}
        />
      </Main>

      {/* Portalled by Radix, so they sit outside the column rather than
          inheriting its padding and its 30rem ceiling. */}
      <HandoverSheet
        delivery={delivery}
        open={handoverOpen}
        onOpenChange={setHandoverOpen}
        onDelivered={(updated) => {
          cache.write(updated);
          cache.settled(updated.id);
          // Fired only AFTER the server answered. A toast that says
          // "Delivered" while the screen still reads "At the door" is a lie
          // the rider catches immediately, and then stops trusting the rest.
          toast.success('Delivered. Nice one.', {
            // `cod` was read before the handover, which is what matters: the
            // server has now written this cash to the rider's balance.
            description: cod
              ? `${formatMoney(delivery.codAmount)} cash is now on your balance.`
              : undefined,
          });
        }}
      />

      <FailSheet
        deliveryId={delivery.id}
        open={failOpen}
        onOpenChange={setFailOpen}
        onFailed={(updated) => {
          cache.write(updated);
          cache.settled(updated.id);
          toast.warning('Marked as not delivered', {
            description: 'Take the bag back to the pharmacy.',
          });
        }}
      />

      <BatchPickupSheet
        jobs={atThisCounter}
        open={batchOpen}
        onOpenChange={setBatchOpen}
        onCollected={() => {
          // Every bag in the batch moved, not just the one on screen.
          for (const job of atThisCounter) cache.invalidate(job.id);
          cache.settled(delivery.id);
        }}
      />

      <PickupCodeDialog code={pickupCode} onDismiss={() => setPickupCode(null)} />
    </>
  );
}

/** The one thing to do next, and the way out when it cannot be done. */
function ActionBar({
  delivery,
  working,
  batchCount,
  onBatch,
  onAdvance,
  onCollect,
  onHandover,
  onGiveUp,
}: {
  delivery: DeliveryDetail;
  working: boolean;
  /** Bags packed and waiting at this counter, this one included. */
  batchCount: number;
  onBatch: () => void;
  onAdvance: (action: () => Promise<DeliveryDetail>) => void;
  onCollect: () => void;
  onHandover: () => void;
  onGiveUp: () => void;
}) {
  if (isTerminal(delivery.status)) {
    return (
      <div className="sticky bottom-0 -mx-gutter border-t border-line bg-page/95 px-gutter py-4 backdrop-blur-xl">
        <p className="flex items-center justify-center gap-2 text-sm text-ink-muted">
          {delivery.status === 'DELIVERED' ? (
            <CircleCheck className="size-4 shrink-0 text-success-ink" aria-hidden="true" />
          ) : (
            <Info className="size-4 shrink-0" aria-hidden="true" />
          )}
          This job is finished
        </p>
      </div>
    );
  }

  return (
    <div
      className={[
        'sticky bottom-0 -mx-gutter border-t border-line bg-page/95 px-gutter backdrop-blur-xl',
        'supports-[not(backdrop-filter:blur(0))]:bg-page',
        // Clear of the tab bar that sits under it.
        'pb-[calc(var(--layout-tabbar)+var(--space-3))] pt-3',
      ].join(' ')}
    >
      {/*
        * One action at a time, still — the batch REPLACES the single pickup
        * rather than sitting beside it. A rider standing at a counter with
        * three bags in front of them has one thing to do, and offering
        * "collect this one" and "collect all three" side by side is how the
        * other two get left behind.
        */}
      {batchCount > 1 && delivery.status === 'ARRIVED_AT_PHARMACY' ? (
        <Button size="xl" block disabled={working} onClick={onBatch}>
          Collect all {batchCount} bags here
        </Button>
      ) : (
        <Button
          size="xl"
          block
          loading={working}
          loadingLabel="Saving"
          onClick={() => primaryAction(delivery, onAdvance, onCollect, onHandover)}
        >
          {PRIMARY_LABELS[delivery.status] ?? 'Refresh'}
        </Button>
      )}

      {/*
       * "Could not deliver" only once the bag is in hand.
       *
       * The state machine has no edge from ASSIGNED, ACCEPTED or
       * ARRIVED_AT_PHARMACY to FAILED — before pickup nothing has changed
       * hands, and a job the rider cannot do goes back to dispatch to
       * reassign. Offering the button there would be offering a 409.
       */}
      {isInHand(delivery.status) ? (
        <Button variant="danger-ghost" size="md" block disabled={working} onClick={onGiveUp}>
          Could not deliver
        </Button>
      ) : (
        <p className="px-2 pb-1 pt-2 text-center text-sm leading-snug text-ink-subtle">
          Cannot take this one? Call the office — they will reassign it.
        </p>
      )}
    </div>
  );
}

const PRIMARY_LABELS: Partial<Record<DeliveryStatus, string>> = {
  ASSIGNED: 'Accept this job',
  ACCEPTED: "I'm at the pharmacy",
  ARRIVED_AT_PHARMACY: "I've collected the bag",
  PICKED_UP: "I'm on my way",
  OUT_FOR_DELIVERY: "I'm at the door",
  ARRIVED_AT_CUSTOMER: 'Hand over the order',
};

function primaryAction(
  delivery: DeliveryDetail,
  onAdvance: (action: () => Promise<DeliveryDetail>) => void,
  onCollect: () => void,
  onHandover: () => void,
) {
  const id = delivery.id;

  switch (delivery.status) {
    case 'ASSIGNED':
      return onAdvance(() => deliveriesApi.accept(id));
    case 'ACCEPTED':
      return onAdvance(() => deliveriesApi.arrivedAtPharmacy(id));
    case 'ARRIVED_AT_PHARMACY':
      // Pickup is the one transition that returns something extra — the
      // handover code, minted here and answered exactly once.
      return onCollect();
    case 'PICKED_UP':
      return onAdvance(() => deliveriesApi.startDelivering(id));
    case 'OUT_FOR_DELIVERY':
      return onAdvance(() => deliveriesApi.arrivedAtCustomer(id));
    case 'ARRIVED_AT_CUSTOMER':
      return onHandover();
    default:
      return undefined;
  }
}
